import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import FeedbackReactions from '@rio-cloud/rio-uikit/FeedbackReactions';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCreateQuickFeedback } from '@/api/feedback';
import { QuickFeedbackCreate, type QuickFeedbackCreateType } from '@/api/schema';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';

export const QuickFeedback = () => {
    const { formatMessage } = useIntl();
    const debugConfig = createDebugConfig();
    const createQuickFeedbackMutation = useCreateQuickFeedback();
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const [hasFeedback, setHasFeedback] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        watch,
        formState: { isLoading },
    } = useForm<QuickFeedbackCreateType>({
        defaultValues: {
            origin: 'ONBOARDING',
            description: undefined,
            rating: undefined,
        },
        mode: 'all',
        resolver: zodResolver(QuickFeedbackCreate),
    });

    const handler = async (values: QuickFeedbackCreateType) => {
        try {
            await createQuickFeedbackMutation.mutateAsync({
                origin: values.origin,
                description: values.description,
                rating: values.rating,
            });

            trackCustomEvent(
                'create_quick_feedback',
                {
                    trigger: 'click',
                    flow_name: 'onboarding',
                    element_state: values.rating,
                },
                debugConfig
            );

            setHasFeedback(true);
        } catch {
            setHasFeedback(false);
            Notification.error(<FormattedMessage id='general.saveChanges.errorMessage' />);
        }
    };

    const isGoodFeedback = (value: string | undefined) => value !== undefined && value === 'GOOD';

    const isBadFeedback = (value: string | undefined) => value !== undefined && value === 'BAD';

    return (
        isVisible && (
            <div className='border-color-light border rounded display-flex align-items-center flex-column margin-top-25 padding-15'>
                <Button
                    bsStyle='muted'
                    bsSize='xs'
                    iconOnly
                    className='margin-bottom-10 margin-left-auto margin-y-0 margin-right-0'
                    onClick={() => setIsVisible(false)}
                    data-testid='close-feedback-button'
                >
                    <span className='uikit-icon icon-remove text-bold' />
                </Button>
                {hasFeedback ? (
                    <>
                        <h3 className='text-center text-color-dark text-normal margin-x-50 margin-top-0 margin-bottom-20'>
                            <FormattedMessage id='quickFeedback.thanksMessage' />
                        </h3>
                        <span className='text-color-white bg-primary padding-10 rounded-circle margin-bottom-20'>
                            <i className='uikit-icon icon-comment text-size-h1' />
                        </span>
                    </>
                ) : (
                    <>
                        <h3 className='text-center text-color-dark text-normal margin-top-0'>
                            <FormattedMessage id='quickFeedback.title' />
                        </h3>
                        <div className='width-100pct'>
                            <div className='max-width-300 padding-x-10 margin-x-auto'>
                                <Controller
                                    name='rating'
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FeedbackReactions
                                            onVoteUp={() => onChange('GOOD')}
                                            onVoteDown={() => onChange('BAD')}
                                            labelPosition='right'
                                            colorFillUp={isGoodFeedback(value) ? 'success' : 'light'}
                                            colorFillDown={isBadFeedback(value) ? 'warning' : 'light'}
                                            colorHoverUp='white'
                                            colorHoverDown='white'
                                            labelDown={
                                                <span className='text-color-darker'>
                                                    <FormattedMessage id='quickFeedback.bad' />
                                                </span>
                                            }
                                            labelUp={
                                                <span className='text-color-darker'>
                                                    <FormattedMessage id='quickFeedback.good' />
                                                </span>
                                            }
                                            itemClassName='hover-bg-lightest rounded padding-5'
                                            size='md'
                                            animated
                                        />
                                    )}
                                />
                            </div>
                            <div>
                                <div className='margin-y-10'>
                                    <div className='display-flex justify-content-between'>
                                        <label className='text-color-dark' htmlFor='quick-feedback-textarea'>
                                            <FormattedMessage id='quickFeedback.textarea.label' />
                                        </label>
                                        <p className='text-color-dark text-size-12 margin-y-0 text-italic'>
                                            <FormattedMessage id='quickFeedback.textarea.maxCharacters' />
                                        </p>
                                    </div>
                                    <Controller
                                        name='description'
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <textarea
                                                id='quick-feedback-textarea'
                                                className='form-control'
                                                placeholder={formatMessage({
                                                    id: 'quickFeedback.textarea.placeholder',
                                                })}
                                                maxLength={1000}
                                                rows={5}
                                                value={value?.toString()}
                                                onChange={e => onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='btn-toolbar justify-content-end'>
                                    <Button
                                        bsStyle='primary'
                                        className='margin-bottom-10'
                                        disabled={watch('rating') === undefined || isLoading}
                                        type='submit'
                                        onClick={handleSubmit(handler)}
                                    >
                                        <span className='uikit-icon icon-send text-bold' />
                                        <span className='padding-right-5 text-size-14'>
                                            <FormattedMessage id='quickFeedback.submit.button' />
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        )
    );
};
